// src/components/configuration/sections/services/ServicesSection.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  AlertCircle,
  Plus,
  Search,
  Wrench
} from 'lucide-react';

// shadcn components
import { Alert, AlertDescription } from '../../../ui/alert';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Spinner } from '../../../ui/spinner';
import { ScrollArea } from '../../../ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';
import { Label } from '../../../ui/label';
import { Textarea } from '../../../ui/textarea';
import { Switch } from '../../../ui/switch';

// Page container
import PageContainer from '../../../ui/layout/PageContainer/PageContainer';

// Local components
import ServiceListItem from './ServiceListItem';
import ServiceEditor from './ServiceEditor';
import { LocalSidebarRail } from '../../../ui/local-sidebar-rail';

// Hook
import { useSettings } from '../../../../hooks/useSettings';
import { cn } from '../../../../utils/cn';

const ServicesSection = () => {
  // Get real data from settings context
  const {
    services,
    createService,
    updateService,
    deleteService,
    createServiceComponent,
    updateServiceComponent,
    deleteServiceComponent,
    loading,
    savingServices,
    savingBaseService,
    savingServiceSettings,
    savingComponents,
    error,
    getServiceById,
    getServiceComponents,
    isConnected
  } = useSettings();

  // Local UI state
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    color_code: '#8B5CF6',
    active: true,
    requires_confirmation: false
  });

  // Color options
  const colorOptions = [
    '#8B5CF6', // Purple
    '#06B6D4', // Cyan
    '#10B981', // Emerald
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#EC4899', // Pink
    '#8B5A2B', // Brown
    '#6366F1'  // Indigo
  ];

  // Auto-select first service when services load
  useEffect(() => {
    if (services && services.length > 0 && !selectedServiceId) {
      setSelectedServiceId(services[0].id);
    }
  }, [services, selectedServiceId]);

  // Filter services based on search
  const filteredServices = services?.filter(service => {
    const matchesSearch = !searchTerm ||
      service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  }) || [];

  // Get selected service object
  const selectedService = selectedServiceId ? getServiceById(selectedServiceId) : null;

  // Saving flag watcher for create flow
  const prevSavingServices = useRef(false);
  const serviceCountBeforeCreate = useRef(null);

  useEffect(() => {
    if (prevSavingServices.current && !savingServices) {
      // savingServices went true→false — create completed
      if (showCreateModal) {
        setShowCreateModal(false);
        setCreateForm({
          name: '',
          description: '',
          color_code: '#8B5CF6',
          active: true,
          requires_confirmation: false
        });
        // Auto-select newly created service (last in list)
        if (services.length > 0 && serviceCountBeforeCreate.current !== null &&
            services.length > serviceCountBeforeCreate.current) {
          setSelectedServiceId(services[services.length - 1].id);
        }
        serviceCountBeforeCreate.current = null;
      }
    }
    prevSavingServices.current = savingServices;
  }, [savingServices, showCreateModal, services]);

  // Handle service operations
  const handleCreateService = () => {
    if (!createForm.name.trim()) return;

    serviceCountBeforeCreate.current = services.length;

    createService({
      name: createForm.name.trim(),
      description: createForm.description?.trim() || null,
      base_price: null,
      base_duration: 60,
      color_code: createForm.color_code,
      active: createForm.active,
      requires_confirmation: createForm.requires_confirmation
    });
  };

  const handleUpdateService = (serviceId, updateData, options) => {
    updateService(serviceId, updateData, options);
  };

  const handleDeleteService = (service) => {
    if (confirm(`Delete "${service.name}"? This will also delete all its add-ons.`)) {
      deleteService(service.id);
      if (selectedServiceId === service.id) {
        setSelectedServiceId(null);
      }
    }
  };

  // Handle component operations
  const handleCreateComponent = (serviceId, componentData) => {
    createServiceComponent(serviceId, componentData);
  };

  const handleUpdateComponent = (serviceId, componentId, updateData) => {
    updateServiceComponent(serviceId, componentId, updateData);
  };

  const handleDeleteComponent = (serviceId, componentId, componentName) => {
    deleteServiceComponent(serviceId, componentId);
  };

  // Loading state
  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Spinner className="size-16 text-primary mx-auto mb-4" />
            <p className="text-foreground text-lg">Loading services...</p>
            <p className="text-muted-foreground text-sm mt-2">Retrieving your service offerings</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer heightMode="viewport" maxWidth="full" padding="none">
      {/* Error Message */}
      {error && (
        <Alert variant="destructive" className="m-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="flex items-center justify-between">
              <span>{error}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content - Split Panel Layout */}
      <div className="h-full flex">
        {/* LEFT PANEL - Services List (shadcn-inspired architecture) */}
        {/* Outer Spacer: Controls layout width, no overflow/border/bg */}
        <div
          className={cn(
            "relative flex-shrink-0 transition-all duration-300 ease-in-out",
            leftPanelCollapsed ? "w-0" : "w-[320px]"
          )}
        >
          {/* Inner Container: Always 320px, slides in/out */}
          <div
            className={cn(
              "absolute inset-y-0 left-0 w-[320px] border-r border-border bg-slate-900/50 flex flex-col transition-transform duration-300 ease-in-out",
              leftPanelCollapsed && "-translate-x-full"
            )}
          >
            {/* Hover Rail - Edge collapse/expand control */}
            <LocalSidebarRail
              collapsed={leftPanelCollapsed}
              onToggle={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
              side="left"
            />

            {/* Header - Administrative Layer */}
            <div className="p-3 border-b flex-shrink-0" data-layer="administrative">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold">Services</h2>
                <Button
                  size="sm"
                  onClick={() => setShowCreateModal(true)}
                  disabled={savingServices}
                  className="layer-button font-medium"
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  Add
                </Button>
              </div>

              {/* Search Bar */}
              <div className="relative group">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-slate-400 transition-colors duration-300 pointer-events-none" />
                <Input
                  type="text"
                  placeholder="Search services..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="layer-input pl-10"
                />
              </div>
            </div>

            {/* Services List - overflow contained to ScrollArea only */}
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-2">
                {filteredServices.length === 0 ? (
                  <div className="text-center py-12">
                    <Wrench className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground text-sm">
                      {searchTerm ? 'No services match your search' : 'No services created yet'}
                    </p>
                  </div>
                ) : (
                  filteredServices.map((service) => (
                    <ServiceListItem
                      key={service.id}
                      service={service}
                      isSelected={selectedServiceId === service.id}
                      onClick={() => setSelectedServiceId(service.id)}
                      onDelete={() => handleDeleteService(service)}
                    />
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* RIGHT PANEL - Service Editor */}
        <div className="flex-1 min-w-0">
          <ServiceEditor
            service={selectedService}
            serviceComponents={selectedService ? getServiceComponents(selectedService.id) : []}
            onUpdateService={handleUpdateService}
            onCreateComponent={handleCreateComponent}
            onUpdateComponent={handleUpdateComponent}
            onDeleteComponent={handleDeleteComponent}
            onCreateService={() => setShowCreateModal(true)}
            savingBaseService={savingBaseService}
            savingServiceSettings={savingServiceSettings}
            savingComponents={savingComponents}
            leftPanelCollapsed={leftPanelCollapsed}
            onToggleLeftPanel={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
          />
        </div>
      </div>

      {/* Create Service Modal */}
      {showCreateModal && (
        <Dialog open={showCreateModal} onOpenChange={(open) => { if (!savingServices) setShowCreateModal(open); }}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="p-2 bg-primary/20 rounded-lg">
                  <Wrench className="w-5 h-5 text-primary" />
                </div>
                Create New Service
              </DialogTitle>
              <DialogDescription>
                Create a new service. You can set pricing and add-ons after creation.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Service Name */}
              <div className="space-y-2">
                <Label htmlFor="create_name">Service Name</Label>
                <Input
                  id="create_name"
                  value={createForm.name}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter service name..."
                  className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                  autoFocus
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="create_description">Description</Label>
                <Textarea
                  id="create_description"
                  value={createForm.description}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe what this service includes..."
                  rows={3}
                  className="resize-none bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                />
              </div>

              {/* Color Picker */}
              <div className="space-y-3">
                <Label>Service Color</Label>
                <div className="flex gap-2 flex-wrap">
                  {colorOptions.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setCreateForm(prev => ({ ...prev, color_code: color }))}
                      className={cn(
                        "w-8 h-8 rounded-lg border-2 transition-all duration-200 hover:scale-110",
                        createForm.color_code === color
                          ? "border-foreground scale-110 shadow-lg"
                          : "border-border hover:border-muted-foreground"
                      )}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>

              {/* Toggle Settings */}
              <div className="space-y-3">
                {/* Active Toggle */}
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border">
                  <div>
                    <Label className="font-medium">Service Active</Label>
                    <p className="text-xs text-muted-foreground">Make this service available for booking</p>
                  </div>
                  <Switch
                    checked={createForm.active}
                    onCheckedChange={(checked) => setCreateForm(prev => ({ ...prev, active: checked }))}
                  />
                </div>

                {/* Requires Confirmation Toggle */}
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border">
                  <div>
                    <Label className="font-medium">Requires Confirmation</Label>
                    <p className="text-xs text-muted-foreground">Appointments need manual approval</p>
                  </div>
                  <Switch
                    checked={createForm.requires_confirmation}
                    onCheckedChange={(checked) => setCreateForm(prev => ({ ...prev, requires_confirmation: checked }))}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                disabled={savingServices}
                className="border-zinc-700 hover:bg-zinc-900 hover:border-zinc-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateService}
                disabled={!createForm.name.trim() || savingServices}
                className="bg-gradient-to-br from-zinc-800 to-zinc-900 border border-zinc-700 shadow-[0_2px_4px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.1)] hover:shadow-[0_0_0_1px_rgba(255,255,255,0.25),0_6px_16px_rgba(255,255,255,0.15),0_4px_8px_rgba(0,0,0,0.5)] hover:border-zinc-500 hover:-translate-y-1 transition-all duration-200 font-medium"
              >
                {savingServices ? 'Creating...' : 'Create Service'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </PageContainer>
  );
};

export default ServicesSection;
